import useFetch from '../../hooks/useFetch'
import usePermissions from '../../hooks/usePermissions'
import { urlApi } from '../../config'
import {
  ContentCopy,
  ContentCopyRounded,
  Person,
  CalendarToday,
  CreditCard,
  AttachMoney,
  Group
} from '@mui/icons-material'
import { useEffect, useState } from 'react'
import Alert from '../../common/Alert'
import LoadingIndicator from '../../common/LoadingIndicator'
import { MenuItem, Select } from '@mui/material'
import RefundPill, { describeRefundReason, getRefundInfo } from '../../common/RefundPill'
import { formatInstant } from '../../utils/formatMoney'
import RefundDialog from '../../common/RefundDialog'
import ResolveRefundDialog from '../../common/ResolveRefundDialog'
import { isManualRefund, refundAfterResolve } from '../../common/ResolveRefundDialog/utils'
import RefundRemainderDialog from '../../common/RefundRemainderDialog'
import { isRemainderRefundable, refundAfterRemainder } from '../../common/RefundRemainderDialog/utils'
import {
  formatMoney,
  isRefundable,
  paymentMethodLabel,
  transactionTotal
} from '../../common/RefundDialog/utils'
import {
  WALLET_PAYMENT_LABEL,
  isWalletCaptureInProgress,
  isWalletPaymentLocked,
  walletPaidAmount,
  walletShareLabel
} from '../../utils/walletPayment'

const HEADER_TONES = {
  approved: { surface: 'bg-green-500 dark:bg-emerald-500/[0.15]', text: 'text-white' },
  refunded: { surface: 'bg-red-600 dark:bg-red-500/[0.15]', text: 'text-white' },
  default: { surface: 'bg-light_gray dark:bg-dk-raised', text: 'text-black dark:text-gray-100' }
}

const headerToneOf = status => HEADER_TONES[status] || HEADER_TONES.default

const Transactions = ({ lobbyId }) => {
  const { loading, data } = useFetch(`${urlApi}/admin/lobbies/transactions/${lobbyId}`)

  return (
    <div className='space-y-3'>
      {loading ? (
        <div className='flex justify-center items-center py-6'>
          <LoadingIndicator size={32} />
        </div>
      ) : (
        <div className='space-y-3'>
          {!data || data.length == 0 ? (
            <div className='text-center py-8'>
              <div className='text-gray-400 dark:text-gray-400 text-lg mb-1'>No hay transacciones</div>
              <div className='text-gray-500 dark:text-gray-400 text-sm'>
                No se encontraron transacciones para este lobby
              </div>
            </div>
          ) : (
            data.map((transaction, index) => (
              <Transaction key={transaction._id} transaction={transaction} />
            ))
          )}
        </div>
      )}
    </div>
  )
}

export const Transaction = ({ transaction }) => {
  const { hasPermission } = usePermissions()
  const canEditLobby = hasPermission('edit-lobby')
  const [joinCompanionAlert, setJoinCompanionAlert] = useState(false)
  const [joinCompanion, setJoinCompanion] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState('wire-transfer')
  const [status, setStatus] = useState(transaction.status)
  const [refund, setRefund] = useState(transaction.refund)
  const [refundDialog, setRefundDialog] = useState(false)
  const [resolveDialog, setResolveDialog] = useState(false)
  const [remainderDialog, setRemainderDialog] = useState(false)
  const metadata = transaction.preference?.metadata || transaction.metadata || {}
  const refundInfo = getRefundInfo({ ...transaction, status, refund })
  const refundDate = refundInfo ? formatInstant(refundInfo.date, refundInfo.currency) : null
  const refundReason = refundInfo ? describeRefundReason(refundInfo) : null
  const headerTone = headerToneOf(status)
  const providerMethod = transaction.payment ? transaction.payment.payment_method_id?.replace('_', ' ') : null
  const walletShare = walletShareLabel({
    walletAmount: walletPaidAmount(transaction),
    currency: metadata.currency,
    paidWithProvider: Boolean(transaction.payment)
  })
  const methodLabel = transaction.payment
    ? [providerMethod, walletShare].filter(Boolean).join(' · ')
    : walletShare
  const canJoinCompanion = canEditLobby && status === 'pending' && !isWalletPaymentLocked(transaction)
  const canRefund = canEditLobby && isRefundable({ status, refund })
  const canResolveRefund = canEditLobby && isManualRefund(refundInfo)
  const canRefundRemainder = canEditLobby && isRemainderRefundable(refundInfo, status)
  const passengerLabel = [
    [transaction.user_info?.first_name, transaction.user_info?.last_name].filter(Boolean).join(' '),
    transaction.user_info?.phone_number
  ]
    .filter(Boolean)
    .join(' · ')

  const getStatusBadge = status => {
    switch (status) {
      case 'approved':
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-sm sm:text-base font-medium bg-green-100 dark:bg-emerald-500/10 text-green-800 dark:text-emerald-400'>
            ✓ Aprobado
          </span>
        )
      case 'refunded':
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-sm sm:text-base font-medium bg-red-100 dark:bg-red-500/10 text-red-800 dark:text-red-400'>
            ↩ Reembolsado
          </span>
        )
      case 'pending':
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-sm sm:text-base font-medium bg-yellow-100 dark:bg-amber-500/10 text-yellow-800 dark:text-amber-400'>
            ⏳ Pendiente
          </span>
        )
      case 'authorized':
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-sm sm:text-base font-medium bg-green-100 dark:bg-emerald-500/10 text-green-800 dark:text-emerald-400'>
            ✓ Autorizado
          </span>
        )
      case 'cancelled':
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-sm sm:text-base font-medium bg-red-100 dark:bg-red-500/10 text-red-800 dark:text-red-400'>
            ↩ Cancelado
          </span>
        )
      default:
        return (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-sm sm:text-base font-medium bg-gray-100 dark:bg-dk-raised text-gray-800 dark:text-gray-100'>
            {status}
          </span>
        )
    }
  }

  const formatCurrency = amount => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: metadata.currency || 'ARS',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const copyPaymentLink = () => {
    const link = `https://www.mercadopago.com.${
      metadata.currency === 'UYU' ? 'uy' : 'ar'
    }/checkout/v1/redirect?preference-id=${transaction.preference_id}`
    navigator.clipboard.writeText(link)
  }

  return (
    <>
      <div className='bg-white dark:bg-dk-surface rounded-lg shadow-sm border border-gray-200 dark:border-dk-border overflow-hidden hover:shadow-md transition-shadow duration-200'>
        <div
          data-testid='transaction-header'
          className={`px-4 py-4 border-b border-gray dark:border-dk-border ${headerTone.surface}`}
        >
          <div className='flex flex-col gap-4 sm:flex-row items-center justify-between'>
            <div className='flex items-center space-x-2'>
              <div className='w-8 h-8 bg-primary rounded-full flex items-center justify-center'>
                <CreditCard className='w-2 h-2 text-white' fontSize='small' />
              </div>
              <div>
                <p className={`text-sm sm:text-base font-semibold ${headerTone.text}`}>
                  {transaction._id}
                </p>
              </div>
            </div>
            <div className='flex items-center space-x-2'>
              {status === 'refunded' && refundReason && (
                <p className={`text-sm font-bold ${headerTone.text}`}>{refundReason}</p>
              )}
              {getStatusBadge(status)}
              <button
                onClick={copyPaymentLink}
                className='inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-primary rounded hover:bg-primary/80 transition-colors duration-200'
              >
                <ContentCopyRounded className='mr-1' />
                Link
              </button>
            </div>
          </div>
        </div>

        {refundInfo && (
          <div className='flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5 border-b border-warm_border dark:border-dk-border bg-cream/70 dark:bg-dk-raised/60 text-left'>
            <span className='text-[11px] uppercase tracking-wider font-medium text-warm_gray dark:text-gray-400'>
              Reembolso
            </span>
            <RefundPill transaction={{ ...transaction, status, refund }} />
            {refundReason && status !== 'refunded' && (
              <span className='text-xs text-espresso dark:text-gray-300'>{refundReason}</span>
            )}
            {refundDate && (
              <span className='text-xs tabular-nums text-warm_gray dark:text-gray-400'>{refundDate}</span>
            )}
          </div>
        )}

        <div className='p-4'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <div className='space-y-3'>
              <div className='bg-light_gray dark:bg-dk-raised rounded-md p-3 text-start'>
                <div className='space-y-1 text-sm'>
                  <p className='text-sm sm:text-base font-medium'>{transaction.user_id}</p>
                  <p className='text-sm sm:text-base'>
                    {transaction.user_info?.first_name} {transaction.user_info?.last_name}
                  </p>
                  <p className='text-sm sm:text-base'>{transaction.user_info?.phone_number}</p>
                </div>
              </div>

              <div className='bg-gray-50 dark:bg-dk-raised rounded-md p-3'>
                <h4 className='text-base mb-1 flex items-center'>
                  <CalendarToday className='mr-2' fontSize='small' />
                  {new Date(transaction.created).toLocaleString('es-AR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </h4>
              </div>
            </div>

            <div className='space-y-3'>
              <div className='bg-light_gray dark:bg-dk-raised rounded-md p-3 text-sm sm:text-base'>
                <div className='space-y-2'>
                  <div className='flex justify-between'>
                    <span className=''>Reserva:</span>
                    <span className='font-medium '>
                      {formatCurrency(metadata.price)}
                    </span>
                  </div>
                  <div className='flex justify-between '>
                    <span className=''>Fee:</span>
                    <span className='font-medium '>
                      {formatCurrency(metadata.fee)}
                    </span>
                  </div>
                  {metadata.discount > 0 && (
                    <div className='flex justify-between '>
                      <span className=''>Descuento:</span>
                      <span className='font-medium '>
                        -{formatCurrency(metadata.discount)}
                      </span>
                    </div>
                  )}
                  <div className='border-t dark:border-dk-border pt-1 mt-1'>
                    <div className='flex justify-between '>
                      <span className='font-medium '>Total:</span>
                      <span className='font-bold '>
                        {formatCurrency(
                          metadata.price +
                            metadata.fee -
                            (metadata.discount || 0)
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className='bg-blue-50 dark:bg-blue-500/10 rounded-md p-3 text-start'>
                <div className='flex items-center justify-between mb-2'>
                  <h4 className='text-base font-medium flex items-center'>
                    <Group className='mr-2' />
                    {metadata.seats}{' '}
                    {metadata.seats > 1 ? 'Asientos' : 'Asiento'}
                  </h4>
                </div>
                {(transaction.payment || walletShare) && (
                  <p className='text-sm'>Método: {methodLabel}</p>
                )}
                {isWalletCaptureInProgress(transaction) && (
                  <p className='text-sm'>Cobro con {WALLET_PAYMENT_LABEL} en curso</p>
                )}
              </div>
            </div>

            <div className='space-y-3'>
              {canJoinCompanion && (
                <button
                  onClick={() => setJoinCompanionAlert(true)}
                  className='w-full bg-primary text-white font-medium py-2 px-3 rounded-md text-sm hover:bg-primary/80 transition-colors duration-200'
                >
                  Unir Acompañante
                </button>
              )}
              {canRefund && (
                <button
                  onClick={() => setRefundDialog(true)}
                  className='w-full bg-primary text-white font-medium py-2 px-3 rounded-md text-sm hover:bg-primary/80 transition-colors duration-200'
                >
                  Reembolsar
                </button>
              )}
              {canResolveRefund && (
                <button
                  onClick={() => setResolveDialog(true)}
                  className='w-full bg-primary text-white font-medium py-2 px-3 rounded-md text-sm hover:bg-primary/80 transition-colors duration-200'
                >
                  Resolver reembolso
                </button>
              )}
              {canRefundRemainder && (
                <button
                  onClick={() => setRemainderDialog(true)}
                  className='w-full bg-primary text-white font-medium py-2 px-3 rounded-md text-sm hover:bg-primary/80 transition-colors duration-200'
                >
                  Reembolsar restante
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <RefundDialog
        open={refundDialog}
        transactionId={transaction._id}
        details={[
          ['Pasajero', passengerLabel],
          ['Total cobrado', formatMoney(transactionTotal(transaction), metadata.currency)],
          ['Pago', paymentMethodLabel(transaction, walletShare)]
        ]}
        alternativePayment={Boolean(transaction.alternative_payment)}
        walletShare={walletShare}
        onClose={() => setRefundDialog(false)}
        onRefunded={result => {
          setRefundDialog(false)
          setStatus(result?.status || 'refunded')
          setRefund(result?.refund || { refund_reason: 'admin_manual_refund' })
        }}
      />

      <ResolveRefundDialog
        open={resolveDialog}
        transactionId={transaction._id}
        details={[
          ['Pasajero', passengerLabel],
          ['A devolver', formatMoney(refundInfo?.amount, refundInfo?.currency)],
          ['Pago', paymentMethodLabel(transaction, walletShare)]
        ]}
        onClose={() => setResolveDialog(false)}
        onResolved={update => {
          if (update.status) setStatus(update.status)
          setRefund(refundAfterResolve(update, { ...transaction, status, refund }))
        }}
      />

      <RefundRemainderDialog
        open={remainderDialog}
        transactionId={transaction._id}
        details={[
          ['Pasajero', passengerLabel],
          ['Total cobrado', formatMoney(transactionTotal(transaction), metadata.currency)],
          ['Ya devuelto', formatMoney(refundInfo?.amount, refundInfo?.currency)]
        ]}
        onClose={() => setRemainderDialog(false)}
        onRefunded={update => {
          if (update.status) setStatus(update.status)
          setRefund(refundAfterRemainder(update, { ...transaction, status, refund }))
        }}
      />

      {joinCompanionAlert && (
        <Alert
          onlyConfirm
          onSubmit={() => setJoinCompanion(true)}
          onClose={() => {
            if (!joinCompanion) setJoinCompanionAlert(false)
          }}
          visible={joinCompanionAlert}
          disabled={joinCompanion}
        >
          {joinCompanion ? (
            <div className='flex justify-center items-center'>
              <JoinCompanion
                transaction={transaction}
                setJoinCompanion={setJoinCompanion}
                setJoinCompanionAlert={setJoinCompanionAlert}
                setStatus={setStatus}
                paymentMethod={paymentMethod}
              />
            </div>
          ) : (
            <div className='text-center'>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2'>Unir Acompañante</h3>
              <p className='text-sm text-gray-600 dark:text-gray-300 mb-4'>
                Selecciona el método de pago para aprobar esta transacción
              </p>
              <Select
                value={paymentMethod}
                size='small'
                placeholder='Seleccionar método de pago'
                onChange={evt => setPaymentMethod(evt.target.value)}
                fullWidth
              >
                <MenuItem value='wire-transfer'>Transferencia Bancaria</MenuItem>
                <MenuItem value='deposit'>Depósito</MenuItem>
              </Select>
            </div>
          )}
        </Alert>
      )}
    </>
  )
}

const JoinCompanion = ({
  transaction,
  setJoinCompanion,
  setJoinCompanionAlert,
  setStatus,
  paymentMethod
}) => {
  const { responseOk, loading } = useFetch(
    `${urlApi}/admin/lobbies/approve-transaction/${transaction._id}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ payment_method: paymentMethod })
    }
  )

  useEffect(() => {
    if (!loading) {
      if (responseOk) {
        alert('Acompañante unido')
        setStatus('approved')
      } else {
        alert('Error al unir acompañante')
      }
      setJoinCompanion(false)
      setJoinCompanionAlert(false)
    }
  }, [loading])

  return <LoadingIndicator size={30} />
}

export default Transactions
